import type {
  Draft, DocumentDetail, DocumentSummary, InvoiceRow, Me, Overview, VendorRow,
} from './types'

const BASE = '/api/v1'

/** The access token lives in memory only.
 *  localStorage would make it readable by any injected script; the refresh
 *  token is an httpOnly cookie the JS never touches. */
let accessToken: string | null = null
let onUnauthorized: (() => void) | null = null

export function setAccessToken(token: string | null) { accessToken = token }
export function getAccessToken() { return accessToken }
export function setUnauthorizedHandler(fn: () => void) { onUnauthorized = fn }

export class ApiError extends Error {
  status: number
  detail: unknown
  errors: Array<{ path?: string; code?: string; message?: string }>
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
    const d = detail as { errors?: Array<Record<string, string>> } | undefined
    this.errors = d?.errors ?? []
  }
}

let refreshing: Promise<boolean> | null = null

async function refresh(): Promise<boolean> {
  // One in-flight refresh at a time; parallel 401s must not each rotate the
  // token, which would trip the server's reuse detection and log the user out.
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) return false
        const body = await r.json()
        accessToken = body.access_token
        return true
      })
      .catch(() => false)
      .finally(() => { refreshing = null })
  }
  return refreshing
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers = new Headers(init.headers)
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(`${BASE}${path}`, {
    ...init, headers, credentials: 'include',
  })

  if (response.status === 401 && retry) {
    if (await refresh()) return request<T>(path, init, false)
    onUnauthorized?.()
    throw new ApiError(401, 'Your session has ended. Sign in again.')
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const body = text ? JSON.parse(text) : null

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body?.title ?? body?.detail ?? `Request failed (${response.status})`,
      body,
    )
  }
  return body as T
}

export const api = {
  // auth
  // `login` accepts a username or an email address.
  login: (login: string, password: string) =>
    request<{ access_token: string; role: string; org_id: string }>(
      '/auth/login', { method: 'POST', body: JSON.stringify({ login, password }) },
    ),
  register: (payload: {
    email: string; username?: string; password: string
    full_name: string; org_name: string
  }) =>
    request<{ access_token: string; role: string; org_id: string }>(
      '/auth/register', { method: 'POST', body: JSON.stringify(payload) },
    ),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<Me>('/auth/me'),
  tryRefresh: refresh,

  // documents
  listDocuments: (params: { status?: string; vendor_id?: string } = {}) => {
    const q = new URLSearchParams()
    if (params.status) q.set('status', params.status)
    if (params.vendor_id) q.set('vendor_id', params.vendor_id)
    const suffix = q.toString() ? `?${q}` : ''
    return request<{ items: DocumentSummary[] }>(`/documents${suffix}`)
  },
  getDocument: (id: string) => request<DocumentDetail>(`/documents/${id}`),
  upload: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<{ id: string; status: string; duplicate: boolean; message?: string }>(
      '/documents', { method: 'POST', body: form },
    )
  },
  getDraft: (id: string) => request<Draft>(`/documents/${id}/draft`),
  patchDraft: (id: string, changes: Record<string, string | null>) =>
    request<Omit<Draft, 'id' | 'status'>>(`/documents/${id}/draft`, {
      method: 'PATCH', body: JSON.stringify({ changes }),
    }),
  approve: (id: string, overrideReason?: string) =>
    request<{ invoice_id: string; invoice_number: string; grand_total: number | null }>(
      `/documents/${id}/approve`,
      { method: 'POST', body: JSON.stringify({ override_reason: overrideReason ?? null }) },
    ),
  reject: (id: string, reason: string) =>
    request<void>(`/documents/${id}/reject`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),
  reextract: (id: string) =>
    request<{ id: string }>(`/documents/${id}/reextract`, { method: 'POST' }),
  deleteDocument: (id: string) =>
    request<void>(`/documents/${id}`, { method: 'DELETE' }),
  fileUrl: (id: string) => `${BASE}/documents/${id}/file?preview=true`,

  // invoices, vendors, analytics
  listInvoices: () => request<{ items: InvoiceRow[] }>('/invoices'),
  listVendors: () => request<{ items: VendorRow[] }>('/vendors'),
  overview: () => request<Overview>('/analytics/overview'),

}

/** SSE progress.
 *  EventSource cannot send an Authorization header, so the fetch-based reader
 *  below is used instead - it also gives us a clean abort path. */
export function streamDocument(
  id: string,
  onEvent: (event: string, data: Record<string, unknown>) => void,
): () => void {
  const controller = new AbortController()

  ;(async () => {
    try {
      const response = await fetch(`${BASE}/documents/${id}/events`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        credentials: 'include',
        signal: controller.signal,
      })
      if (!response.ok || !response.body) return

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const frames = buffer.split('\n\n')
        buffer = frames.pop() ?? ''
        for (const frame of frames) {
          if (frame.startsWith(':')) continue // heartbeat
          let event = 'message'
          let payload = ''
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim()
            else if (line.startsWith('data:')) payload += line.slice(5).trim()
          }
          if (payload) {
            try { onEvent(event, JSON.parse(payload)) } catch { /* ignore */ }
          }
        }
      }
    } catch {
      // aborted or the stream dropped; the caller falls back to polling
    }
  })()

  return () => controller.abort()
}
